import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { RoleGuard, ApplicationRole } from '../core';
import { AboutComponent } from './about/about.component';
import { CharityComponent } from './charity/charity.component';
import { ContributeComponent } from './contribute/contribute.component';
import { ContributionFailedComponent } from './contribution-failed/contribution-failed.component';
import { ContributionSucceededComponent } from './contribution-succeeded/contribution-succeeded.component';
import { CookiePolicyComponent } from './cookie-policy/cookie-policy.component';
import { CorporateComponent } from './corporate/corporate.component';
import { DepositFailedComponent } from './deposit-failed/deposit-failed.component';
import { DepositSucceededComponent } from './deposit-succeeded/deposit-succeeded.component';
import { ErrorComponent } from './error/error.component';
import { EventContributionsComponent } from './event-contributions/event-contributions.component';
import { EventDetailComponent } from './event-detail/event-detail.component';
import { FaqComponent } from './faq/faq.component';
import { ForbiddenComponent } from './forbidden/forbidden.component';
import { GiftCardsComponent } from './gift-cards/gift-cards.component';
import { HowItWorksComponent } from './how-it-works/how-it-works.component';
import { NotFoundComponent } from './not-found/not-found.component';
import { PartnersComponent } from './partners/partners.component';
import { PrivacyComponent } from './privacy/privacy.component';
import { SearchComponent } from './search/search.component';
import { SupportComponent } from './support/support.component';
import { SocialComponent } from './social/social.component';
import { TermsConditionsComponent } from './terms-conditions/terms-conditions.component';
import {
  SocialShareComponent, EventUpdatesComponent, EventCommentsComponent, GiftCardsListComponent, PartnerListComponent
} from './components';
import { BlankLayoutComponent, MainLayoutComponent } from '../shared/components';
import { GiftCardComponent } from './gift-card/gift-card.component';
import { EventSuspendedComponent } from './event-suspended/event-suspended.component';
import { ContributionPendingComponent } from './contribution-pending/contribution-pending.component';
import { DepositPendingComponent } from './deposit-pending/deposit-pending.component';
import { GiftShareGiftCardComponent } from './giftshare-gift-card/giftshare-gift-card.component';
import { CompetitionsComponent } from './competitions/competitions.component';

const routes: Routes = [
  {
    path: '',
    component: MainLayoutComponent,
    children: [
      { path: 'despre-noi', component: AboutComponent, data: { title: 'Despre Echipa GiftShare', description: 'GiftShare inseamna oameni. Descopera echipa din spatele platformei si cum lucram impreuna pentru a aduce mai multa bucurie prin daruri cu sens.' } },
      { path: 'cum-functioneaza', component: HowItWorksComponent, data: { title: 'Cum Functioneaza GiftShare', description: 'Afla cum folosesti GiftShare in orice situatie: cand oferi un cadou, contribui la unul de grup sau primesti un card cadou. Simplu, rapid si cu sens.' } },
      { path: 'intrebari-frecvente', component: FaqComponent, data: { title: 'Intrebari Frecvente despre GiftShare', description: 'Ai nelamuriri despre cum functioneaza GiftShare? Vezi raspunsurile la cele mai frecvente intrebari legate de cont, cadouri, plati si livrare.' } },
      { path: 'politica-de-confidentialitate', component: PrivacyComponent, data: { title: 'Politica de Confidentialitate', description: 'Afla cum GiftShare colecteaza, foloseste si protejeaza datele tale personale. Transparenta si siguranta sunt esentiale pentru noi.' } },
      { path: 'termeni-si-conditii', component: TermsConditionsComponent, data: { title: 'Termeni si Conditii', description: 'Citeste termenii si conditiile de utilizare ale platformei GiftShare. Afla care sunt drepturile, obligatiile si regulile de functionare.' } },
      { path: 'cookies', component: CookiePolicyComponent, data: { title: 'Politica de Utilizare Cookie-uri', description: 'Aflati cum foloseste GiftShare cookie-uri pentru a imbunatati experienta utilizatorilor si pentru analiza traficului pe site.' } },
      { path: 'contact', component: SupportComponent, data: { title: 'Contacteaza Echipa GiftShare', description: 'Ai intrebari sau ai nevoie de ajutor? Contacteaza echipa GiftShare prin formular, email sau retele sociale. Iti raspundem cu drag!' } },
      { path: 'social', component: SocialComponent, data: { title: 'Postari Social Media', description: 'Vezi cele mai recente postari de pe retelele sociale GiftShare. Raspandim inspiratie, idei de cadouri si fapte bune din comunitatea noastra.' } },
      { path: 'parteneri', component: PartnersComponent, data: { title: 'Devino Partener GiftShare si Creste Vanzarile cu Carduri Cadou', description: 'Promoveaza-ti magazinul fara buget de marketing: parteneriat flexibil, expunere crescuta si acces la utilizatori care folosesc carduri cadou GiftShare.' } },
      { path: 'caritate', component: CharityComponent, canActivate: [RoleGuard], data: { title: 'Evenimente Caritabile cu GiftShare pentru Cauze in Care Crezi', description: 'Organizeaza campanii de strangere de fonduri sau sustine ONG-uri direct prin GiftShare. Transforma darurile in fapte bune. Creeaza un eveniment caritabil.', allowRoles: [ApplicationRole.Member] } },
      { path: 'card-cadou-giftshare', component: GiftShareGiftCardComponent, data: { title: 'Card Cadou GiftShare – Solutia Simpla pentru Daruri Inspirate', description: 'Descopera cardul cadou GiftShare – flexibil, digital si valabil in reteaua partenerilor. Ofera libertatea de a alege cadoul perfect, fara batai de cap.' } },
      { path: 'card-cadou-corporate', component: CorporateComponent, data: { title: 'Card Cadou GiftShare pentru Beneficii Extrasalariale Angajati', description: 'Card cadou deductibil fiscal si usor de gestionat. Angajatii isi aleg cadoul preferat. Simplu, digital si eficient. Contacteaza un consultant GiftShare.' } },
      { path: 'card-cadou-concursuri', component: CompetitionsComponent, data: { title: 'Card Cadou GiftShare pentru Concursuri si Premii Personalizate', description: 'Ofera un card cadou flexibil ca premiu la concursuri. Castigatorii isi aleg singuri premiul dintr-o retea variata de magazine partenere GiftShare.' } },

      { path: 'evenimente-publice', component: SearchComponent, data: { title: 'Evenimente Publice si Campanii Sociale', description: 'Descopera evenimentele publice organizate prin GiftShare. Contribuie la campanii sociale, sprijina cauze si fa parte din schimbare prin daruire.' } },
      { path: 'eveniment/:eventCode', component: EventDetailComponent, data: { title: 'Eveniment' } },
      { path: 'contributie/:eventCode', component: ContributeComponent, data: { title: 'Contributie' } },
      { path: 'contributii/:eventCode', component: EventContributionsComponent, data: { title: 'Lista contributii' } },
      { path: 'eveniment-suspendat', component: EventSuspendedComponent, data: { title: 'Eveniment suspendat' } },

      { path: 'carduri-cadou', component: GiftCardsComponent, canActivate: [RoleGuard], data: { title: 'Carduri Cadou GiftShare de la Parteneri pentru Orice Ocazie', description: 'Exploreaza colectia de carduri cadou de la partenerii GiftShare. Cadouri flexibile, 100% online, perfecte pentru orice stil, buget sau moment special.', allowRoles: [ApplicationRole.Member] } },
      { path: 'carduri-cadou/:slug', component: GiftCardComponent, canActivate: [RoleGuard], data: { title: 'Cadoul perfect', allowRoles: [ApplicationRole.Member] } },
      { path: 'cadoul-perfect', redirectTo: 'carduri-cadou' },
      {
        path: 'cadoul-perfect/:slug', redirectTo: (route) => {
          const slug = route.params['slug'];
          return `/carduri-cadou/${slug}`;
        }
      },

      { path: 'plata-esuata/:id', component: ContributionFailedComponent, data: { title: 'Plata esuata' } },
      { path: 'plata-finalizata/:id', component: ContributionSucceededComponent, data: { title: 'Plata finalizata cu succes' } },
      { path: 'plata-in-verificare/:id', component: ContributionPendingComponent, data: { title: 'Plata in verificare' } },
      { path: 'depozit-esuat/:id', component: DepositFailedComponent, data: { title: 'Depozit esuat' } },
      { path: 'depozit-finalizat/:id', component: DepositSucceededComponent, data: { title: 'Depozit finalizat cu succes' } },
      { path: 'depozit-in-verificare/:id', component: DepositPendingComponent, data: { title: 'Depozit in verificare' } },
    ]
  },
  {
    path: '',
    component: BlankLayoutComponent,
    children: [
      { path: 'error', component: ErrorComponent, data: { title: 'Eroare' } },
      { path: 'forbidden', component: ForbiddenComponent, data: { title: 'Acces interzis' } },
      { path: 'not-found', component: NotFoundComponent, data: { title: 'Pagina inexistenta' } },
    ]
  }

];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule]
})
export class StaticRoutingModule { }

export const components = [
  AboutComponent,
  HowItWorksComponent,
  FaqComponent,
  PrivacyComponent,
  TermsConditionsComponent,
  CorporateComponent,
  CompetitionsComponent,
  PartnersComponent,
  PartnerListComponent,
  CharityComponent,
  SupportComponent,
  CookiePolicyComponent,
  EventDetailComponent,
  SearchComponent,
  ContributeComponent,
  EventContributionsComponent,
  ErrorComponent,
  ForbiddenComponent,
  NotFoundComponent,
  ContributionFailedComponent,
  ContributionSucceededComponent,
  ContributionPendingComponent,
  DepositFailedComponent,
  DepositSucceededComponent,
  DepositPendingComponent,
  SocialComponent,
  SocialShareComponent,
  EventUpdatesComponent,
  EventCommentsComponent,
  EventSuspendedComponent,
  GiftCardsListComponent,
  GiftCardsComponent,
  GiftCardComponent,
  GiftShareGiftCardComponent
];

